import { useState, useEffect } from "react";
import { C } from "../constants/colors";
import { buildTopicsMap, getAvailableSubjects, TOPICS_MAP_FALLBACK } from "../constants/curriculum";
import { LearnMode } from "./LearnMode";
import { EnhancedLearnMode } from "./EnhancedLearnMode";
import { MOTION_SVG_MAP } from "./motion-slides";
import { GEOMETRY_SVG_MAP } from "./geometry-slides";
import { LIGHT_SVG_MAP } from "./light-slides";
import { INTERACTIVE_MAP } from "./interactive-diagrams";
import { SCIENCE_SVG_MAP } from "./science-slides";
import { fetchEnrichedChapter, enrichedToSlides, findNcertChapter, loadEnrichmentConfig, getChapterKey } from "../enrichment";
import { getDashboardStats, getChapterConceptsAttempted } from "../progress";

// Pick the right SVG diagram set based on chapter/subject — merges static + interactive
function getSvgMap(chapter, subject, studentClass) {
  const ch = (chapter || "").toLowerCase();
  const maps = { ...INTERACTIVE_MAP }; // interactive always available
  // Motion (Class 9 Science)
  if (ch.includes("motion") && !ch.includes("magnetic") && subject === "Science" && String(studentClass) === "9") return { ...maps, ...MOTION_SVG_MAP };
  // Light, Human Eye (Class 10 Science)
  if (ch.includes("light") || ch.includes("human eye") || ch.includes("colourful")) return { ...maps, ...LIGHT_SVG_MAP };
  // Electricity (Class 10 Science)
  if (ch.includes("electric") && subject === "Science") return { ...maps, ...SCIENCE_SVG_MAP };
  // Magnetic Effects (Class 10 Science)
  if (ch.includes("magnetic") && subject === "Science") return { ...maps, ...SCIENCE_SVG_MAP };
  // Sound (Class 9 Science)
  if (ch.includes("sound") && subject === "Science") return { ...maps, ...SCIENCE_SVG_MAP };
  // Atoms/Cell/Structure (Class 9 Science)
  if (subject === "Science" && (ch.includes("atom") || ch.includes("structure") || ch.includes("fundamental unit") || ch.includes("cell"))) return { ...maps, ...SCIENCE_SVG_MAP };
  // Geometry chapters (Maths)
  if (subject === "Maths" && (
    ch.includes("triangle") || ch.includes("circle") || ch.includes("coordinate") ||
    ch.includes("lines and angle") || ch.includes("quadrilateral") ||
    ch.includes("parallelogram") || ch.includes("trigonometry") ||
    ch.includes("applications of trigonometry") || ch.includes("areas related") ||
    ch.includes("heron") || ch.includes("euclid")
  )) return { ...maps, ...GEOMETRY_SVG_MAP };
  // Surface areas / volumes (Maths)
  if (subject === "Maths" && (ch.includes("surface") || ch.includes("volume"))) return { ...maps, ...SCIENCE_SVG_MAP };
  return maps;
}

export const IconResume = () => (
  <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="12" stroke="white" strokeWidth="2" fill="white" fillOpacity="0.2"/>
    <path d="M13 10.5l9 5.5-9 5.5V10.5z" fill="white"/>
  </svg>
);
export const IconHomework = () => (
  <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
    <rect x="7" y="4" width="16" height="22" rx="3" fill="white" fillOpacity="0.15" stroke="white" strokeWidth="2"/>
    <path d="M11 11h10M11 15h10M11 19h6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="23" cy="23" r="5" fill="white" fillOpacity="0.9"/>
    <path d="M21 23l1.5 1.5 2.5-2.5" stroke="#d97706" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
export const IconExplain = () => (
  <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="13" r="8" fill="white" fillOpacity="0.15" stroke="white" strokeWidth="2"/>
    <path d="M12 13c0-2.2 1.8-4 4-4s4 1.8 4 4c0 1.5-.8 2.8-2 3.5V19h-4v-2.5C12.8 15.8 12 14.5 12 13z" fill="white" fillOpacity="0.4"/>
    <path d="M13.5 22h5M14.5 25h3" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 9v2M16 15v1" stroke="white" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
export const IconTest = () => (
  <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
    <rect x="5" y="5" width="10" height="10" rx="2.5" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="2"/>
    <rect x="17" y="5" width="10" height="10" rx="2.5" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="2"/>
    <rect x="5" y="17" width="10" height="10" rx="2.5" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="2"/>
    <rect x="17" y="17" width="10" height="10" rx="2.5" fill="white" fillOpacity="0.35" stroke="white" strokeWidth="2"/>
    <path d="M19.5 22l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const SUBJECT_COLORS_MAP = {
  Maths:     { iconBg:C.amberSoft, iconColor:C.amberDark, accent:C.amberDark, accentMid:C.amberMid, light:C.amberSoft, border:C.amberBorder, text:C.amberDeep, deep:C.amberDeep },
  Science:   { iconBg:C.primarySoft, iconColor:C.primaryMuted, accent:C.primaryMid, accentMid:C.primaryMid, light:C.primarySoft, border:C.primaryLight, text:C.primaryDeep, deep:C.primaryDeep },
  Geography: { iconBg:C.greenSoft, iconColor:C.greenMid, accent:C.greenMid, accentMid:C.greenMid, light:C.greenSoft, border:"#C0DD97", text:"#27500A", deep:"#173404" },
  Economics: { iconBg:C.purpleSoft, iconColor:C.purpleMid, accent:C.purpleMid, accentMid:C.purpleMid, light:C.purpleSoft, border:"#CECBF6", text:"#3C3489", deep:"#26215C" },
};

export const SubjectIconMap = {
  Maths: ({color="white"}) => (
    <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
      <path d="M6 14h16M14 6v16M8 8l12 12M20 8L8 20" stroke={color} strokeWidth="2.2" strokeLinecap="round"/>
    </svg>
  ),
  Science: ({color="white"}) => (
    <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
      <path d="M11 4v8L5 20a2 2 0 001.7 3h14.6A2 2 0 0023 20l-6-8V4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9 4h10" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <circle cx="10" cy="18" r="1.5" fill={color}/>
      <circle cx="16" cy="20" r="1" fill={color}/>
    </svg>
  ),
  Geography: ({color="white"}) => (
    <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="10" stroke={color} strokeWidth="2"/>
      <path d="M4 14h20M14 4c-3 4-3 12 0 20M14 4c3 4 3 12 0 20" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  Economics: ({color="white"}) => (
    <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
      <path d="M4 20l6-7 5 4 9-11" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="10" cy="13" r="2" fill={color} fillOpacity="0.5"/>
      <circle cx="15" cy="17" r="2" fill={color} fillOpacity="0.5"/>
    </svg>
  ),
};

// ── Explain Page (Direction D: Warm Confident) ───────────────────────────────
export function ExplainPage({ profile, lang, setLang, onHome, onStartPractice, mode="explain", curriculumData, initialSubject, initialChapter }) {
  const ff = lang==="hi"?"'Noto Sans Devanagari','Poppins',sans-serif":"'Poppins',sans-serif";
  // Restore state if coming back from chat (cameFromExplain).
  // If a subject was preselected on the home screen, skip the subject picker entirely.
  // If both subject AND chapter are preselected (Resume tap), jump straight to the chapter detail page.
  const [step, setStep] = useState(
    initialSubject && initialChapter ? "topic"
    : profile?.explainSubject && profile?.cameFromExplain ? "topic"
    : initialSubject ? "chapter"
    : "subject"
  );
  const [subject, setSubject] = useState(initialSubject || profile?.explainSubject || null);
  // If we entered with a preselected subject, there's no subject picker to go back to —
  // the "Back" button on the chapter screen should return home instead.
  const cameWithPreselectedSubject = !!initialSubject;
  const [chapter, setChapter] = useState(initialChapter || profile?.explainChapter || null);
  const [topic, setTopic] = useState(null);
  const [enrichedSlides, setEnrichedSlides] = useState(null);
  const [enrichLoading, setEnrichLoading] = useState(false);
  // CHAPTER-CARD-METADATA-FIX-APPLIED: real concept/slide counts derived from
  // the enriched chapter JSON so the chapter-detail screen doesn't show stale
  // curriculum.json numbers (e.g. Trig curriculum says 3 concepts; enriched has 4).
  const [chapterStats, setChapterStats] = useState(null);
  // COMING-SOON-APPLIED: track which chapter keys have v2 enrichment available; the rest render as "Coming soon".
  const [v2ChapterKeys, setV2ChapterKeys] = useState(null); // null = loading; Set = ready
  useEffect(() => {
    let cancelled = false;
    loadEnrichmentConfig().then(cfg => {
      if (cancelled) return;
      const v2 = new Set(
        Object.entries(cfg.chapters || {})
          .filter(([, ver]) => ver === "v2")
          .map(([key]) => key)
      );
      setV2ChapterKeys(v2);
    }).catch(() => { if (!cancelled) setV2ChapterKeys(new Set()); });
    return () => { cancelled = true; };
  }, []);
  const [loadQuote] = useState(() => {
    const q = [
      { emoji: "🐢", text: "Why did the turtle finish the race? Because he never stopped." },
      { emoji: "🧠", text: "Your brain has 86 billion neurons cheering you on right now." },
      { emoji: "🚀", text: "NASA makes mistakes too. They just call them 'learning opportunities.'" },
      { emoji: "🐜", text: "An ant can carry 50x its weight. You can carry one more concept." },
      { emoji: "🦉", text: "The owl doesn't know everything. It just stays curious." },
    ];
    return q[Math.floor(Math.random() * q.length)];
  });
  const isTest = mode==="test";
  const studentClass = profile?.studentClass || 10;

  // Build TOPICS_MAP dynamically from curriculum for this student's class
  const TOPICS_MAP = curriculumData ? buildTopicsMap(curriculumData, studentClass) : TOPICS_MAP_FALLBACK;
  const availableSubjects = curriculumData ? getAvailableSubjects(curriculumData, studentClass) : ["Maths","Science","Geography","Economics"];

  // Get full concept objects for a chapter (for LearnMode)
  const getChapterConcepts = (subj, chapterName) => {
    if (!curriculumData?.CBSE?.[String(studentClass)]?.[subj]) return [];
    const subjData = curriculumData.CBSE[String(studentClass)][subj];
    const ch = subjData.chapters.find(c => c.name === chapterName);
    return ch?.concepts || [];
  };

  // Load enriched data for a chapter
  const loadEnriched = async (subj, chapterName, thenStep) => {
    setEnrichLoading(true);
    setEnrichedSlides(null);
    const ncertCh = findNcertChapter(curriculumData, studentClass, subj, chapterName);
    if (ncertCh) {
      const data = await fetchEnrichedChapter(studentClass, subj, ncertCh, chapterName);
      if (data) {
        const slides = enrichedToSlides(data);
        setEnrichedSlides(slides);
      }
    }
    setEnrichLoading(false);
    setStep(thenStep);
  };

  const fetchExplanation = async (topicName, subj) => {
    if(isTest){ onStartPractice(topicName, subj); return; }
    setTopic(topicName);
    // Load enriched data, then go to learn-single
    loadEnriched(subj, chapter, "learn-single");
  };

  // CHAPTER-CARD-METADATA-FIX-APPLIED: fetch enriched chapter when the user lands on
  // the chapter-detail screen so the stats row shows real numbers. Falls back to the
  // curriculum-derived estimate while loading or if the enriched file 404s.
  useEffect(() => {
    if (step !== "topic" || !subject || !chapter || !curriculumData) return;
    let cancelled = false;
    setChapterStats(null);
    (async () => {
      const ncertCh = findNcertChapter(curriculumData, studentClass, subject, chapter);
      if (!ncertCh) return;
      const data = await fetchEnrichedChapter(studentClass, subject, ncertCh, chapter);
      if (cancelled || !data) return;
      const slideData = enrichedToSlides(data);
      if (!slideData) return;
      const conceptCount = Array.isArray(slideData.concepts) ? slideData.concepts.length : 0;
      // V2-TOPIC-LIST-APPLIED: capture real concept names from v2 enriched data so the
      // chapter-detail topic list reflects v2 reality (e.g. Circles has 4 concepts in v2
      // but only 1 in legacy curriculum.json). Names match what learn-single deep-link
      // expects, so each tap drops into the correct concept.
      const conceptNames = (slideData.concepts || []).map(c => c.name).filter(Boolean);
      // Total slides = sum of per-concept slides + chapter-level extras
      // (chapter-intro / funfact / animation = ~2 slides) + (questionBank intro + items)
      const conceptSlides = (slideData.concepts || []).reduce((acc, c) => acc + (c.slides?.length || 0), 0);
      const qbCount = Array.isArray(slideData.questionBank) ? slideData.questionBank.length : 0;
      const qbExtras = qbCount > 0 ? 1 : 0; // intro slide
      const chapterExtras = 2; // funfact + animation; intro slide is conditional but small
      const slideCount = conceptSlides + chapterExtras + qbCount + qbExtras;
      // ~1 min per ~5 slides reads about right for the current bite-size pacing.
      const minutes = Math.max(5, Math.round(slideCount / 5));
      setChapterStats({ conceptCount, slideCount, minutes, conceptNames });
    })();
    return () => { cancelled = true; };
  }, [step, subject, chapter, curriculumData, studentClass]);

  const sc = subject ? SUBJECT_COLORS_MAP[subject] : SUBJECT_COLORS_MAP.Science;
  const SubIcon = subject ? SubjectIconMap[subject] : SubjectIconMap.Science;

  // Back button — clean, on white
  const BackBtn = ({label, onClick, dark=false}) => (
    <button onClick={onClick} style={{background:dark?"rgba(255,255,255,0.15)":"none",cursor:"pointer",padding:"6px 12px",borderRadius:10,color:dark?"white":C.textMid,fontSize:13,fontWeight:600,marginBottom:dark?16:0,display:"flex",alignItems:"center",gap:6,border:dark?"none":`none`,fontFamily:ff}}>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
      {label}
    </button>
  );

  // ── Subject selection ──
  if(step==="subject") return (
    <div style={{minHeight:"calc(100vh / 0.92)", zoom: 0.92,background:"#6AAAC8",fontFamily:ff}}>
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"14px 20px",display:"flex",alignItems:"center",gap:10}}>
        <BackBtn label={lang==="hi"?"वापस":"Back"} onClick={onHome}/>
        <span style={{fontWeight:600,fontSize:15,color:C.text,flex:1}}>
          {isTest ? (lang==="hi"?"टेस्ट के लिए विषय चुनें":"Choose subject to test") : (lang==="hi"?"विषय चुनें":"Choose a subject")}
        </span>
        <span style={{fontSize:11,padding:"3px 10px",borderRadius:12,background:"rgba(255,255,255,0.2)",color:"white"}}>
          {lang==="hi"?`कक्षा ${profile?.studentClass}`:`Class ${profile?.studentClass}`} · {profile?.board}
        </span>
      </div>
      <div style={{padding:"20px",maxWidth:460,margin:"0 auto"}}>
        <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",marginBottom:16}}>{lang==="hi"?"पढ़ाई शुरू करने के लिए विषय चुनें":"Pick a subject to start learning"}</div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {availableSubjects.filter(sub => SUBJECT_COLORS_MAP[sub]).map((sub)=>{
            const scc = SUBJECT_COLORS_MAP[sub];
            const Icon = SubjectIconMap[sub];
            const chapters = TOPICS_MAP[sub] ? Object.keys(TOPICS_MAP[sub]).length : 0;
            // Mock progress — vertical bars
            const done = sub==="Maths"?3:sub==="Science"?1:0;
            return (
              <div key={sub} onClick={()=>{setSubject(sub);setStep("chapter");}} style={{
                border:`1px solid rgba(140,110,60,0.06)`,borderRadius:16,padding:18,cursor:"pointer",
                background:"rgba(255,255,255,0.93)",transition:"transform .12s, border-color .12s",
                borderLeft:`4px solid ${scc.accent}`,
                boxShadow:"0 2px 8px rgba(140,110,60,0.08), 0 1px 2px rgba(140,110,60,0.06)",
              }}
                onMouseOver={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.borderColor=scc.accent;}}
                onMouseOut={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.borderColor=C.border;}}
              >
                <div style={{display:"flex",alignItems:"center",gap:14}}>
                  <div style={{width:48,height:48,borderRadius:14,background:scc.iconBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <Icon color={scc.iconColor}/>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:16,fontWeight:600,color:C.text,marginBottom:3}}>{sub}</div>
                    <div style={{fontSize:12,color:C.textMute}}>{chapters} chapters</div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    {done > 0 ? (
                      <div style={{display:"flex",gap:3}}>
                        {Array.from({length:chapters}).map((_,i)=>(
                          <div key={i} style={{width:6,height:20,borderRadius:3,background:i<done?scc.accent:`rgba(140,110,60,0.08)`}}/>
                        ))}
                      </div>
                    ) : (
                      <span style={{fontSize:12,color:C.textMute}}>{lang==="hi"?"नया":"New"}</span>
                    )}
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke={C.textMute} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ── Chapter selection ──
  if(step==="chapter") {
    const chapters = Object.keys(TOPICS_MAP[subject]||{});
    // Real progress from localStorage (no mock data) — completed flag drives "done",
    // any concept-level quiz attempts drive "progress" with a real fraction.
    // Pure visit without quiz attempts counts as zero progress (no indicator).
    const realStats = getDashboardStats(studentClass);
    const realChapters = realStats?.allChapters || {};
    // COMING-SOON-APPLIED: v2 set drives availability — non-v2 chapters render as disabled "Coming soon".
    // While the config is loading (v2ChapterKeys === null) treat everything as available so the page
    // doesn't briefly flash "Coming soon" everywhere.
    const v2Set = v2ChapterKeys; // null while loading; Set once loaded
    const chapterProgress = chapters.map((ch) => {
      const key = `${studentClass}-${subject}-${ch}`;
      const record = realChapters[key];
      const total = getChapterConcepts(subject, ch).length;
      const attempted = getChapterConceptsAttempted(studentClass, subject, ch);
      // Resolve v2 availability via the slugified chapter key
      const ncert = findNcertChapter(curriculumData, studentClass, subject, ch);
      const slugKey = ncert ? getChapterKey(studentClass, subject, ncert, ch) : null;
      const isV2 = v2Set === null ? true : (slugKey ? v2Set.has(slugKey) : false);
      let status;
      if (!isV2) status = "coming-soon";
      else if (record?.completed) status = "done";
      else if (attempted > 0) status = "progress";
      else status = "new";
      return { status, attempted, total, isV2 };
    });
    const chapterStatus = chapterProgress.map(p => p.status);
    // Denominator counts only available (v2) chapters per option (b) — "1 of 4 chapters done"
    // reflects what's actually doable, not aspirational total.
    const availableCount = chapterProgress.filter(p => p.isV2).length;
    const doneCount = chapterStatus.filter(s=>s==="done").length;

    return (
      <div style={{minHeight:"calc(100vh / 0.92)", zoom: 0.92,background:"#6AAAC8",fontFamily:ff}}>
        {/* White header with subject icon */}
        <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"14px 20px"}}>
          <BackBtn label={lang==="hi"?"वापस":"Back"} onClick={()=> cameWithPreselectedSubject ? onHome?.() : setStep("subject")}/>
        </div>
        <div style={{padding:"20px 20px 12px",maxWidth:460,margin:"0 auto"}}>
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:16}}>
            <div style={{width:48,height:48,borderRadius:14,background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <SubIcon color="white"/>
            </div>
            <div>
              <div style={{fontSize:20,fontWeight:700,color:"white",letterSpacing:"-0.03em"}}>{subject}</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.75)",marginTop:4}}>{doneCount} of {availableCount} chapters done</div>
            </div>
          </div>
          {/* Continuous progress bar — hidden at zero progress (no decorated empty state).
              Denominator is availableCount (v2 chapters only), so the fill reflects real reachable progress. */}
          {doneCount > 0 && availableCount > 0 && (
            <div style={{height:7,borderRadius:4,background:"rgba(255,255,255,0.2)",overflow:"hidden",marginBottom:4}}>
              <div style={{height:"100%",width:`${(doneCount/availableCount)*100}%`,background:"white",borderRadius:4,transition:"width 0.4s ease"}}/>
            </div>
          )}
        </div>

        <div style={{padding:"0 20px",maxWidth:460,margin:"0 auto"}}>
          <div style={{height:1,background:"rgba(255,255,255,0.15)",marginBottom:16}}/>

          {/* Chapter list with progress states */}
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {chapters.map((ch,i)=>{
              const cp = chapterProgress[i];
              const status = cp.status;
              const attempted = cp.attempted;
              const total = cp.total;
              const fraction = total > 0 ? Math.min(1, attempted / total) : 0;
              const ringCirc = 2 * Math.PI * 13.5;
              const isComingSoon = status === "coming-soon";
              // Completed = green bg, in-progress = amber bg, new = neutral, coming-soon = visibly faded grey
              const cardBg = isComingSoon ? "rgba(255,255,255,0.35)"
                : status==="done" ? C.primarySoft
                : status==="progress" ? C.amberSoft
                : C.surface;
              const cardBorder = isComingSoon ? "rgba(255,255,255,0.18)"
                : status==="done" ? C.primarySoftBorder
                : status==="progress" ? C.amberBorder
                : C.border;
              const statusText = isComingSoon ? (lang==="hi"?"जल्द आ रहा है":"Coming soon")
                : status==="done" ? (lang==="hi"?"पूरा हो गया! शाबाश":"Completed! Well done")
                : status==="progress" ? (lang==="hi"?`${total} में से ${attempted} अवधारणाएं`:`${attempted} of ${total} concepts done`)
                : (lang==="hi"?"शुरू करने के लिए तैयार":"Ready to start");
              const statusColor = isComingSoon ? "rgba(140,110,60,0.45)"
                : status==="done" ? C.primaryMid
                : status==="progress" ? C.amberDark
                : C.textMute;

              return (
                <div key={ch}
                  onClick={isComingSoon ? undefined : ()=>{setChapter(ch);setStep("topic");}}
                  style={{
                    cursor: isComingSoon ? "default" : "pointer",
                    padding:"14px 16px", borderRadius:14,
                    background:cardBg, border:`0.5px solid ${cardBorder}`,
                    display:"flex", alignItems:"center", gap:12,
                    transition:"transform .12s",
                    opacity: isComingSoon ? 0.55 : 1,
                  }}
                  onMouseOver={isComingSoon ? undefined : e=>e.currentTarget.style.transform="translateY(-1px)"}
                  onMouseOut={isComingSoon ? undefined : e=>e.currentTarget.style.transform="translateY(0)"}
                >
                  {/* Status indicator — none for unstarted/coming-soon chapters (no decorated empty state) */}
                  {status==="done" ? (
                    <div style={{width:32,height:32,borderRadius:"50%",background:C.primaryMid,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                  ) : status==="progress" ? (
                    <div style={{width:32,height:32,flexShrink:0,position:"relative",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <span style={{fontSize:9,fontWeight:700,color:C.amberDark,position:"relative",zIndex:1}}>{attempted}/{total}</span>
                      <svg width="32" height="32" viewBox="0 0 32 32" style={{position:"absolute",top:0,left:0,transform:"rotate(-90deg)"}} fill="none">
                        <circle cx="16" cy="16" r="13.5" stroke="rgba(186,117,23,0.15)" strokeWidth="2.5" fill="none"/>
                        <circle cx="16" cy="16" r="13.5" stroke={C.amber} strokeWidth="2.5" fill="none" strokeLinecap="round"
                          strokeDasharray={ringCirc}
                          strokeDashoffset={ringCirc*(1-fraction)}/>
                      </svg>
                    </div>
                  ) : null}
                  <div style={{flex:1}}>
                    <div style={{fontSize:14,fontWeight:600,color: isComingSoon ? "rgba(60,46,21,0.40)" : (status==="done"?sc.deep:status==="progress"?C.amberDeep:C.text)}}>{ch}</div>
                    <div style={{fontSize:12,color:statusColor,marginTop:2}}>{statusText}</div>
                  </div>
                  {/* Right-side indicator: clock icon for coming-soon (visible at-a-glance signal),
                      chevron for tappable chapters. */}
                  {isComingSoon ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(140,110,60,0.45)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9"/>
                      <polyline points="12 7 12 12 15 14"/>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke={statusColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── Topic selection ──
  if(step==="topic") {
    const concepts = getChapterConcepts(subject, chapter);
    const fallbackTopics = TOPICS_MAP[subject]?.[chapter]||[];
    // V2-TOPIC-LIST-APPLIED: prefer the v2 enriched concept names (real chapter contents)
    // over the legacy curriculum topics list. Falls back to curriculum while v2 is still
    // loading or for non-v2 chapters.
    const v2Topics = chapterStats?.conceptNames || [];
    const topicsList = v2Topics.length > 0 ? v2Topics : fallbackTopics;
    // CHAPTER-CARD-METADATA-FIX-APPLIED: prefer enriched-JSON-derived counts
    // (chapterStats), fall back to curriculum-based estimates while it loads.
    const displayConceptCount = chapterStats?.conceptCount ?? concepts.length;
    const displaySlideCount = chapterStats?.slideCount ?? (concepts.length * 5);
    const displayMinutes = chapterStats?.minutes ?? Math.max(5, concepts.length * 4);

    return (
    <div style={{minHeight:"calc(100vh / 0.92)", zoom: 0.92,background:"#6AAAC8",fontFamily:ff}}>
      <div style={{padding:"14px 20px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <BackBtn label={subject} onClick={()=>setStep("chapter")} dark={true}/>
      </div>
      <div style={{padding:"8px 24px 24px",maxWidth:460,margin:"0 auto"}}>

        {/* Chapter header */}
        <div style={{marginBottom:24}}>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.7)",marginBottom:4}}>{lang==="hi"?"अध्याय":"Chapter"}</div>
          <div style={{fontSize:22,fontWeight:700,color:"white",letterSpacing:"-0.02em"}}>{chapter}</div>
        </div>

        {/* What you'll explore */}
        <div style={{background:"white",borderRadius:20,padding:"20px 22px",marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:600,color:"#64748b",marginBottom:14}}>
            {lang==="hi"?"आप क्या explore करेंगे":"What you'll explore"}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {topicsList.map((tp,i)=>(
              <div key={tp} style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:28,height:28,borderRadius:8,background:"#E8F5EE",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <span style={{fontSize:12,fontWeight:600,color:"#1A7A52"}}>{i+1}</span>
                </div>
                <span style={{fontSize:14,color:C.text,lineHeight:1.4}}>{tp}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Stats row with icons — slides count removed; concepts + approx time only */}
        <div style={{display:"flex",gap:10,marginBottom:20}}>
          <div style={{flex:1,background:"rgba(255,255,255,0.93)",borderRadius:14,padding:"14px 14px",textAlign:"center",border:"1px solid rgba(140,110,60,0.06)"}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={sc.accent} strokeWidth="2" strokeLinecap="round" style={{margin:"0 auto 6px",display:"block"}}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
            <div style={{fontSize:18,fontWeight:700,color:sc.accent}}>{displayConceptCount}</div>
            <div style={{fontSize:11,color:"#7A7A6A",marginTop:2}}>{lang==="hi"?"अवधारणाएं":"concepts"}</div>
          </div>
          <div style={{flex:1,background:"rgba(255,255,255,0.93)",borderRadius:14,padding:"14px 14px",textAlign:"center",border:"1px solid rgba(140,110,60,0.06)"}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={sc.accent} strokeWidth="2" strokeLinecap="round" style={{margin:"0 auto 6px",display:"block"}}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <div style={{fontSize:18,fontWeight:700,color:sc.accent}}>~{displayMinutes}</div>
            <div style={{fontSize:11,color:"#7A7A6A",marginTop:2}}>{lang==="hi"?"मिनट":"min"}</div>
          </div>
        </div>

        {/* Start learning CTA */}
        {!isTest && concepts.length > 0 && (
          <button onClick={()=>loadEnriched(subject, chapter, "learn")} style={{
            width:"100%",padding:"16px",background:"linear-gradient(135deg, #1A7A52, #228B5A)",color:"white",
            border:"none",borderRadius:16,fontSize:16,fontWeight:700,
            cursor:"pointer",fontFamily:ff,marginBottom:20,transition:"transform .12s",
            boxShadow:"0 4px 12px rgba(26,122,82,0.25)",
          }}
            onMouseDown={e=>e.currentTarget.style.transform="scale(0.98)"}
            onMouseUp={e=>e.currentTarget.style.transform="scale(1)"}
          >
            {lang==="hi"?"सीखना शुरू करें →":"Start learning →"}
          </button>
        )}

        {/* Or pick a specific topic — TOPIC-CARD-V2-APPLIED: match chapter list card style
            (solid white surface, subtle border, no heavy shadow). Tap deep-links to v2 slides for that
            concept via fetchExplanation → loadEnriched → "learn-single" step (already wired). */}
        {topicsList.length > 0 && (
          <>
            <div style={{fontSize:12,fontWeight:500,color:"rgba(255,255,255,0.85)",marginBottom:12,textAlign:"center"}}>
              {lang==="hi"?"या कोई टॉपिक चुनें":"or jump to a specific topic"}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {topicsList.map((tp,i)=>(
                <div key={tp} onClick={()=>{setTopic(tp);fetchExplanation(tp,subject);}} style={{
                  cursor:"pointer", padding:"14px 16px", borderRadius:14,
                  background: C.surface, border: `0.5px solid ${C.border}`,
                  display:"flex", alignItems:"center", gap:12,
                  transition:"transform .12s",
                }}
                  onMouseOver={e=>e.currentTarget.style.transform="translateY(-1px)"}
                  onMouseOut={e=>e.currentTarget.style.transform="translateY(0)"}
                >
                  <div style={{width:28,height:28,borderRadius:8,background:"#E8F5EE",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <span style={{fontSize:12,fontWeight:600,color:"#1A7A52"}}>{i+1}</span>
                  </div>
                  <span style={{fontSize:14,fontWeight:600,color:C.text,flex:1}}>{tp}</span>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke={C.textMute} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
    );
  }

  // ── Explanation screen ──
  // ── Loading state with quirky quote ──
  if (enrichLoading) return (
    <div style={{minHeight:"calc(100vh / 0.92)", zoom: 0.92,background:"#6AAAC8",fontFamily:ff,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:20,padding:40}}>
      <div style={{display:"flex",gap:6}}>{[0,1,2].map(i=><div key={i} style={{width:10,height:10,borderRadius:"50%",background:sc.accent,animation:`td 1.2s ease-in-out ${i*0.2}s infinite`}}/>)}</div>
      <div style={{background:"white",borderRadius:16,padding:"16px 20px",display:"flex",alignItems:"center",gap:10,maxWidth:320}}>
        <span style={{fontSize:20}}>{loadQuote.emoji}</span>
        <div style={{fontSize:13,color:"#64748b",lineHeight:1.5}}>{loadQuote.text}</div>
      </div>
    </div>
  );

  // ── Single concept slide mode (when topic is tapped) ──
  if(step==="learn-single" && topic) {
    // Use enriched data if available
    if (enrichedSlides) {
      const singleConcept = enrichedSlides.concepts.find(c => c.name === topic);
      if (singleConcept) {
        const filteredSlideData = { ...enrichedSlides, concepts: [singleConcept] };
        // Pass Motion SVGs if it's the Motion chapter
        const svgs = getSvgMap(chapter, subject, studentClass);
        return (
          <EnhancedLearnMode
            slideData={filteredSlideData}
            svgGenerators={svgs}
            profile={profile}
            lang={lang}
            onBack={()=>setStep("topic")}
            onHome={onHome}
            onPractice={(topicName, subj) => onStartPractice(topicName, subj, chapter)}
          />
        );
      }
    }
    // Fallback: use LearnMode with single concept from curriculum
    const allConcepts = getChapterConcepts(subject, chapter);
    const singleConcept = allConcepts.find(c => c.name === topic);
    return (
      <LearnMode
        concepts={singleConcept ? [singleConcept] : allConcepts.slice(0, 1)}
        chapter={chapter}
        subject={subject}
        profile={profile}
        lang={lang}
        setLang={setLang}
        onBack={()=>setStep("topic")}
        onPractice={(topicName, subj) => onStartPractice(topicName, subj, chapter)}
      />
    );
  }

  // ── Full chapter learn mode (slides + quiz) ──
  if(step==="learn") {
    // Use enriched data if available
    if (enrichedSlides) {
      const svgs = getSvgMap(chapter, subject, studentClass);
      return (
        <EnhancedLearnMode
          slideData={enrichedSlides}
          svgGenerators={svgs}
          profile={profile}
          lang={lang}
          onBack={()=>setStep("topic")}
          onHome={onHome}
          onPractice={(topicName, subj) => onStartPractice(topicName, subj, chapter)}
        />
      );
    }
    // Fallback: basic LearnMode from curriculum JSON
    return (
      <LearnMode
        concepts={getChapterConcepts(subject, chapter)}
        chapter={chapter}
        subject={subject}
        profile={profile}
        lang={lang}
        setLang={setLang}
        onBack={()=>setStep("topic")}
        onPractice={(topicName, subj) => onStartPractice(topicName, subj, chapter)}
      />
    );
  }

  return null;
}


// ── Test App ──────────────────────────────────────────────────────────────────
